import React, { useEffect, useState, useCallback } from "react";
import { Save, Truck, AlertCircle, Plug, CheckCircle2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, isAuthError } from "@/lib/api";
import { describeTestConnection, describeTestConnectionError } from "@/lib/jubelioTestConnection";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";

const DEFAULT_COURIER_MAP = {
  jne: "JNE",
  jnt: "JNT",
  sicepat: "SICEPAT",
  anteraja: "ANTERAJA",
  gosend: "GOSEND",
  grab: "GRAB",
};

const BASE_URL_PROD = "https://api-shipment.jubelio.com";

export default function JubelioSettings() {
  const [cfg, setCfg] = useState({
    enabled: false,
    // Sandbox default = false (production) untuk tenant BARU.
    // Tenant existing yang punya sandbox=true di DB tetap dihormati oleh
    // load() dan disimpan apa adanya oleh save(). UI tidak menampilkan
    // toggle-nya sehingga tidak dapat mengganti mode secara tidak sengaja.
    sandbox: false,
    use_real_sandbox: false,
    client_id: "",
    client_secret: "",
    base_url: BASE_URL_PROD,
    warehouse_id: "",
    sender_name: "",
    sender_phone: "",
    sender_address: "",
    sender_city: "",
    sender_postal_code: "",
    courier_mapping: { ...DEFAULT_COURIER_MAP },
  });
  const [clientSecretMasked, setClientSecretMasked] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [active, setActive] = useState(false);
  // Tes Koneksi memakai setting TERSIMPAN di server — siap hanya bila backend menyatakan siap.
  const [testReady, setTestReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/settings/jubelio");
      if (data && Object.keys(data).length) {
        setCfg((prev) => {
          const next = { ...prev, ...data };
          next.courier_mapping = {
            ...DEFAULT_COURIER_MAP,
            ...(data.courier_mapping || {}),
          };
          // Preserve stored base_url when present. Only default to
          // production URL when tenant has never saved one.
          if (!next.base_url) next.base_url = BASE_URL_PROD;
          return next;
        });
        setClientSecretMasked(!!data._client_secret_set);
        setActive(!!data._active);
        setTestReady(!!data._test_ready);
      }
    } catch (e) {
      if (!isAuthError(e)) toast.error(formatApiError(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setLoading(true);
    try {
      const payload = { ...cfg };
      payload.client_id = (payload.client_id || "").trim();
      payload.client_secret = (payload.client_secret || "").trim();
      // Jangan kirim client_secret bila masih bentuk mask ATAU kosong → backend preserve secret tersimpan.
      if (!payload.client_secret || (clientSecretMasked && payload.client_secret.includes("***"))) {
        delete payload.client_secret;
      }
      // Tenant frontend TIDAK boleh mengirim field developer
      // `enable_create_awb`. Backend akan menurunkannya otomatis (derive)
      // saat enabled=true. Lihat admin_routes.admin_update_jubelio_settings.
      delete payload.enable_create_awb;
      await api.put("/admin/settings/jubelio", payload);
      toast.success("Pengaturan Jubelio berhasil disimpan");
      setTestResult(null);
      await load();
    } catch (e) {
      if (!isAuthError(e)) toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const testConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/admin/settings/jubelio/test-connection");
      // Backend mengembalikan kode kategori aman — tidak ada token/secret/payload upstream.
      const res = describeTestConnection(data);
      setTestResult(res);
      if (res.ok) toast.success(res.title);
      else toast.error(res.title);
    } catch (e) {
      if (isAuthError(e)) return;
      const res = describeTestConnectionError(e);
      setTestResult(res);
      toast.error(res.title);
    } finally {
      setTesting(false);
    }
  };

  const updateCourier = (key, value) => {
    setCfg((c) => ({ ...c, courier_mapping: { ...c.courier_mapping, [key]: value } }));
  };

  return (
    <AdminLayout title="Jubelio">
      <div className="max-w-3xl space-y-6" data-testid="admin-jubelio-settings">
        {/* Status banner — tenant-friendly copy */}
        <Card
          className={`p-6 border flex gap-3 ${active ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`}
          data-testid="jubelio-status-banner"
        >
          {active ? (
            <CheckCircle2 className="text-emerald-600 shrink-0 mt-0.5" size={20} />
          ) : (
            <AlertCircle className="text-amber-600 shrink-0 mt-0.5" size={20} />
          )}
          <div className={`text-sm ${active ? "text-emerald-900" : "text-amber-900"}`}>
            {active ? (
              <>
                <strong>Jubelio Aktif</strong>
                <div className="mt-0.5">Akun Jubelio Anda sudah aktif dan siap digunakan untuk proses pengiriman.</div>
              </>
            ) : (
              <>
                <strong>Jubelio Belum Aktif</strong>
                <div className="mt-0.5">Aktifkan Jubelio dan lengkapi informasi akun untuk mulai menggunakan layanan pengiriman.</div>
              </>
            )}
          </div>
        </Card>

        <Card className="p-6 space-y-5">
          <div className="flex items-center gap-3">
            <Truck className="text-[#4A2E1F]" size={24} />
            <h2 className="text-lg font-semibold">Pengaturan Jubelio</h2>
          </div>
          <p className="text-sm text-[#5C5C5C] -mt-2">
            Hubungkan akun Jubelio Anda untuk mengatur pengiriman dan membuat nomor resi pesanan melalui Dynicty.
          </p>

          <div className="flex items-center justify-between p-4 bg-[#FAF9F6] rounded-xl border border-[#E5E5E5]" data-tour="jubelio-enable">
            <div>
              <div className="font-semibold text-sm">Aktifkan Jubelio</div>
              <div className="text-xs text-[#7A5440] mt-0.5">Aktifkan agar Jubelio dapat menghitung ongkir dan membuat resi pesanan.</div>
            </div>
            <Switch checked={cfg.enabled} onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })} data-testid="jubelio-enable-toggle" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div data-tour="jubelio-client-id">
              <Label>Client ID</Label>
              <Input
                value={cfg.client_id || ""}
                onChange={(e) => setCfg({ ...cfg, client_id: e.target.value })}
                placeholder="Client ID dari akun Jubelio Anda"
                data-testid="jubelio-client-id"
              />
            </div>
            <div data-tour="jubelio-client-secret">
              <Label>Client Secret</Label>
              <div className="relative">
                <Input
                  type={showSecret ? "text" : "password"}
                  value={cfg.client_secret || ""}
                  onChange={(e) => { setCfg({ ...cfg, client_secret: e.target.value }); setClientSecretMasked(false); }}
                  placeholder={clientSecretMasked ? "(tersimpan — ketik nilai baru untuk mengganti)" : "Client Secret dari akun Jubelio Anda"}
                  data-testid="jubelio-client-secret"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowSecret((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-[#7A5440] hover:text-[#4A2E1F] focus:outline-none"
                  title={showSecret ? "Sembunyikan" : "Tampilkan"}
                  data-testid="jubelio-secret-toggle"
                  aria-label={showSecret ? "Sembunyikan Client Secret" : "Tampilkan Client Secret"}
                >
                  {showSecret ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {clientSecretMasked && <div className="text-xs text-[#5A8F3D] mt-1">Client Secret tersimpan. Ketik baru untuk mengganti.</div>}
            </div>
            <div className="md:col-span-2" data-tour="jubelio-warehouse-id">
              <Label>Warehouse ID</Label>
              <Input
                value={cfg.warehouse_id || ""}
                onChange={(e) => setCfg({ ...cfg, warehouse_id: e.target.value })}
                placeholder="Warehouse ID dari akun Jubelio Anda"
                data-testid="jubelio-warehouse-id"
              />
            </div>
          </div>

          <div className="pt-2 border-t border-[#F0E5C0]" data-tour="jubelio-sender">
            <h3 className="font-semibold text-sm mb-3">Informasi Pengirim</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Input placeholder="Nama Pengirim" value={cfg.sender_name || ""} onChange={(e) => setCfg({ ...cfg, sender_name: e.target.value })} data-testid="jubelio-sender-name" />
              <Input placeholder="Nomor HP Pengirim" value={cfg.sender_phone || ""} onChange={(e) => setCfg({ ...cfg, sender_phone: e.target.value })} data-testid="jubelio-sender-phone" />
              <Input className="md:col-span-2" placeholder="Alamat Lengkap Pengirim" value={cfg.sender_address || ""} onChange={(e) => setCfg({ ...cfg, sender_address: e.target.value })} data-testid="jubelio-sender-address" />
              <Input placeholder="Kota Pengirim" value={cfg.sender_city || ""} onChange={(e) => setCfg({ ...cfg, sender_city: e.target.value })} data-testid="jubelio-sender-city" />
              <Input placeholder="Kode Pos" value={cfg.sender_postal_code || ""} onChange={(e) => setCfg({ ...cfg, sender_postal_code: e.target.value })} data-testid="jubelio-sender-postal" />
            </div>
          </div>

          <div className="pt-2 border-t border-[#F0E5C0]">
            <h3 className="font-semibold text-sm mb-1">Pemetaan Kurir</h3>
            <p className="text-xs text-[#7A5440] mb-3">Sesuaikan nama kurir toko Anda dengan nama kurir yang dikenali Jubelio.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {Object.keys(DEFAULT_COURIER_MAP).map((k) => (
                <div key={k} className="flex gap-2 items-center">
                  <div className="w-28 text-xs uppercase font-bold tracking-wider text-[#5C5C5C]">{k}</div>
                  <Input
                    value={cfg.courier_mapping?.[k] || ""}
                    onChange={(e) => updateCourier(k, e.target.value)}
                    placeholder={DEFAULT_COURIER_MAP[k]}
                    data-testid={`jubelio-courier-${k}`}
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="pt-2 space-y-2">
            <p className="text-xs text-[#7A5440]" data-testid="jubelio-test-hint">
              Simpan pengaturan terlebih dahulu, lalu klik Tes Koneksi. Tes menggunakan Client ID dan Client Secret
              yang sudah tersimpan dan memverifikasinya langsung ke server Jubelio. Warehouse ID tidak diperlukan untuk tes ini.
            </p>
            <div className="flex flex-wrap gap-2 items-center">
              <Button
                onClick={testConnection}
                disabled={testing || !testReady}
                variant="outline"
                title={testReady ? "Verifikasi Client ID & Client Secret tersimpan ke Jubelio" : "Simpan dan aktifkan Jubelio terlebih dahulu"}
                data-testid="jubelio-test-btn"
                data-tour="jubelio-test"
              >
                <Plug size={16} className="mr-2" /> {testing ? "Mengetes..." : "Tes Koneksi"}
              </Button>
              <Button onClick={save} disabled={loading} data-testid="jubelio-save-btn" data-tour="jubelio-save" className="bg-[#4A2E1F] text-white">
                <Save size={16} className="mr-2" /> {loading ? "Menyimpan..." : "Simpan Pengaturan"}
              </Button>
              {testResult && (
                <div
                  className={`text-sm px-3 py-1.5 rounded-lg ${testResult.ok ? "bg-emerald-100 text-emerald-800 border border-emerald-200" : "bg-red-100 text-red-800 border border-red-200"}`}
                  data-testid="jubelio-test-result"
                  data-code={testResult.code}
                  role={testResult.ok ? "status" : "alert"}
                >
                  <strong>{testResult.title}</strong> {testResult.body}
                </div>
              )}
            </div>
          </div>
        </Card>
      </div>
    </AdminLayout>
  );
}
